import { errorToast } from "@/v2-ui/toast"
import Selector from "@/v2-backoffice-client/components/selector"
import type { SelectorItemProps } from "@/v2-backoffice-client/components/selector"
import {
  countCrossCustomerMemberships,
  formatCountOfTotal,
  formatOwnerNames,
} from "@/v2-backoffice-client/customer/ownership/customer.ownership.utils"
import type { TenantFragment } from "@/v2-backoffice-client/tenant/__types__/tenant.fragment"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"
import { useTenantUserAddManyMutation } from "@/v2-backoffice-client/tenant/user/add/many/tenant.user.add.many.mutation"

export type TenantUserAddManySelectorItemProps = Omit<
  SelectorItemProps,
  "value" | "children" | "onSelect" | "warning"
> & {
  tenant: TenantFragment
  users: UserFragment[]
  userTenantTypeCode?: number
  onSelect?(): void
}

/**
 * One team the selected users can be added to. Takes a confirmation when
 * some of them are owned by another customer than the team's: a
 * membership across customers is for one company sharing content with
 * another's user, and otherwise the wrong team.
 */
function TenantUserAddManySelectorItem(
  props: TenantUserAddManySelectorItemProps,
) {
  const { tenant, users, userTenantTypeCode, onSelect, ...selectorItemProps } =
    props

  const [addMany, { loading, error }] = useTenantUserAddManyMutation()
  errorToast(error, {
    title: "Failed to add users to team",
  })

  const crossing = countCrossCustomerMemberships(users, tenant)

  return (
    <Selector.Item
      isLoading={loading}
      value={`${tenant.Name} ${tenant.Id}`}
      warning={
        crossing.count > 0
          ? `${formatCountOfTotal(crossing.count, crossing.total, "selected user")} ${crossing.count === 1 ? "is" : "are"} owned by ${formatOwnerNames(crossing.ownerNames)}, not by this team's customer.`
          : undefined
      }
      confirmContent="Add anyway?"
      onSelect={() => {
        addMany({
          variables: {
            tenantId: tenant.Id,
            userIds: users.map((user) => user.Id),
            userTenantTypeCode,
          },
        })
        onSelect?.()
      }}
      {...selectorItemProps}
    >
      <Selector.ItemText
        isRemoved={tenant.IsDeleted}
        primary={tenant.Name}
        secondary={`${tenant.CustomerName} - ${tenant.UserCount} users`}
      />
    </Selector.Item>
  )
}

export default TenantUserAddManySelectorItem
